import { useNavigation } from '@react-navigation/native'
import { IS_WEB } from 'constants/platform'
import { useTheme } from 'contexts'
import { LinearGradient } from 'expo-linear-gradient'
import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { StyleSheet } from 'react-native'
import { v4 as uuidv4 } from 'uuid'

import { EditableUpspaceCover } from 'partials/EditableUpspaceCover'
import { SponsorItem } from 'partials/UpspaceHeader/subcomponents/SponsorItem'

import { Avatar } from 'widgets/Avatar'
import { defaultAvatarImage } from 'widgets/Avatar/defaultAvatarImage'
import { Bio } from 'widgets/Bio'
import { Box } from 'widgets/Box'
import { FlatList } from 'widgets/FlatList'
import { Icon } from 'widgets/Icon'
import { Image } from 'widgets/Image'
import { Pressable } from 'widgets/Pressable'
import { PressableText } from 'widgets/PressableText'
import { Text } from 'widgets/Text'

import { useClientSubscriptions } from 'hooks/useClientSubscriptions'
import { useCurrentUser } from 'hooks/useCurrentUser'
import { useDMRoom } from 'hooks/useDMRoom'
import { useGroup } from 'hooks/useGroup'
import { useLeaderboard } from 'hooks/useLeaderboard'
import { useMaxWidth } from 'hooks/useMaxWidth'
import { usePendingMembership } from 'hooks/usePendingMembership'
import { usePendingMemberships } from 'hooks/usePendingMemberships'
import { useQueryClient } from 'hooks/useQueryClient'
import { useUpspaceRoom } from 'hooks/useUpspaceRoom'

import { links } from 'utils/constants'
import { defaultPostThumbnailUrl } from 'utils/media'
import commonStyles from 'utils/styles/commonStyles'
import { Toast } from 'utils/toast'
import { createPartialUser } from 'utils/user/createPartialUser'

import { useFocusMode } from 'contexts/FocusModeContext'
import { useInfoModal } from 'contexts/InfoModalContext'

import { Program, ProgramType } from 'types/program'
import { UserType } from 'types/user'

interface UpspaceHeaderProps {
  user: UserType
  program?: Program
  params: any
  onShare: () => void
  onBackPress: () => void
  onOptionsMenuPress: () => void
  type?: ProgramType
}

const styles = StyleSheet.create({
  flex: { flex: 1 },
  gradient: {
    width: '100%',
    height: '25%',
  },
  br35: {
    borderRadius: 35,
  },
})

export const UpspaceHeader = ({
  user,
  program,
  params,
  onShare,
  onBackPress,
  onOptionsMenuPress,
  type = ProgramType.GROUP,
}: UpspaceHeaderProps) => {
  const leaderboardPositionColors = ['#FFA800', '#B7B7B7', '#944912']
  const { width: maxWidth } = useMaxWidth()
  const { sessionId } = params
  const { data: currentUser } = useCurrentUser()
  const { meta } = useGroup({ userId: user?.id })
  const { hasSetupGroup } = meta
  const theme = useTheme()
  const { createRoom } = useDMRoom({})
  const { data: upspaceRoom } = useUpspaceRoom({ program, userId: currentUser?.id })

  const programTags = useMemo(() => {
    if (!program?.tags) return []
    const tags = Object.values(program.tags)
    return [].concat(...tags)
  }, [program?.tags])

  const focusMode = useFocusMode()
  const isFocusMode = focusMode.group && user && focusMode.group.trainer.id === user.id

  const { data: leaderboard, isLoading: isLeaderboardLoading } = useLeaderboard({
    trainerId: type !== ProgramType.PROGRAM ? user?.id : undefined,
    programId: type === ProgramType.PROGRAM ? program?.id : undefined,
    type,
  })

  const isCurrentUsersUpspace = currentUser?.id === user?.id

  const queryClient = useQueryClient()
  const navigation = useNavigation<any>()

  const { data: subscriptions } = useClientSubscriptions({
    userId: currentUser?.id,
  })

  useEffect(() => {
    if (sessionId && hasSetupGroup) queryClient.invalidateQueries('subscriptions')
  }, [queryClient, sessionId, hasSetupGroup])

  const isSubscribed = useMemo(() => {
    if (!hasSetupGroup) return null

    return subscriptions?.find((subscription) => subscription.program.trainer.id === user?.id)
  }, [subscriptions, user?.id, hasSetupGroup])

  const isSubscriptionLoading = !!sessionId && !isSubscribed

  const { addPendingMembership } = usePendingMembership({
    userId: currentUser?.id,
    program,
    trainer: user,
  })

  const { data: pendingMemberships } = usePendingMemberships({ userId: currentUser?.id })
  const pendingPrograms = pendingMemberships ? pendingMemberships.map((pm) => pm.program) : []
  const isPendingMembership: boolean | undefined = pendingPrograms?.some(
    (p) => p?.trainer?.id === user?.id,
  )

  const handleAddPendingMembership = () => {
    if (isPendingMembership || !isSubscribed) {
      firePendingSnack()
    }
    if (!isSubscribed) {
      addPendingMembership.mutateAsync()
    }
  }

  const handleViewPress = () => {
    navigation.navigate(links.MEMBERSHIPS_PAGE)
  }

  const firePendingSnack = () => {
    Toast.info(
      '',
      `The creator must accept you into their Group. We will notify you when you’re in.`,
      true,
      {},
      true,
      'bottom',
      handleViewPress,
      'View',
    )
  }

  const handleEdit = () => {
    type === ProgramType.GROUP && editGroup()
    type === ProgramType.PROGRAM && editProgram()
  }

  const editGroup = () => {
    navigation.navigate(links.GROUP_SETTINGS_PAGE)
  }

  const editProgram = () => {
    navigation.navigate(links.EDIT_PROGRAM_PAGE, { programId: program?.id })
  }

  const handleLeaderboardPress = () => {
    navigation.navigate(links.LEADERBOARD_PAGE, {
      trainerId: user.id,
    })
  }

  const createChatRoom = useCallback(async () => {
    if (currentUser && user) {
      if (type === ProgramType.PROGRAM) {
        navigation.navigate(links.CHAT_ROOM, { roomId: upspaceRoom?.id })
        return
      }

      const { id: roomId } = await createRoom.mutateAsync({
        participants: [createPartialUser(currentUser), createPartialUser(user)],
      })

      navigation.navigate(links.CHAT_ROOM, { roomId })
    }
  }, [currentUser, user, type, createRoom, navigation, upspaceRoom?.id])

  const [buttonTitle, setButtonTitle] = useState<string>('Loading...')

  useEffect(() => {
    if (!isSubscriptionLoading && isSubscribed) {
      setButtonTitle('Member')
    } else if (!isSubscriptionLoading && !isPendingMembership && !isSubscribed) {
      setButtonTitle('Join')
    } else if (!isSubscriptionLoading && isPendingMembership) {
      setButtonTitle('Pending')
    }
  }, [isPendingMembership, isSubscribed, isSubscriptionLoading])

  const navigateToProfile = () => {
    navigation.navigate(links.MEMBER_PROFILE_PAGE, {
      username: user.username,
    })
  }

  const infoModalRef = useInfoModal()

  const showInfo = () => {
    infoModalRef?.current?.present({
      title: 'What’s a program?',
      infoText:
        'Programs are curated collections of actions. You can follow the program step by step to achieve your goals. Each program also has its own exclusive feed, group chat, and leaderboard.',
    })
  }

  const renderInfoButton = (right: number, bottom: number) => {
    return (
      <Pressable onPress={showInfo} style={{ position: 'absolute', right, bottom }}>
        <Box
          flexDirection='row'
          width={110}
          height={30}
          borderRadius='lg'
          align='center'
          justifyContent='space-between'
          backgroundColor='greyLight'
          style={{ opacity: 0.9 }}
          padding={{ left: 'smd', right: 'xs' }}
        >
          <Text color='black' fontSize='sm'>
            {type === ProgramType.PROGRAM ? 'Program' : 'Group'}
          </Text>
          <Icon name='info' color='greyDarkest' />
        </Box>
      </Pressable>
    )
  }

  return (
    <Box>
      <Box
        width={maxWidth}
        height={maxWidth}
        backgroundColor='greyLight'
        flexDirection='column'
        justifyContent='flex-start'
      >
        <Box
          testID='NavBarContainer'
          zIndex={3}
          width='100%'
          padding='md'
          position='absolute'
          flexDirection='row'
          align='center'
          justifyContent='space-between'
        >
          {!isFocusMode ? (
            <Pressable onPress={onBackPress}>
              <Icon name='arrow-left' color='white' size={24} />
            </Pressable>
          ) : (
            <Box width={24} />
          )}

          <Box flex={1} flexDirection='row' align='center' justifyContent='center'>
            <Text
              fontSize='lg'
              color='white'
              numberOfLines={1}
              align='center'
              bold
              ellipsizeMode='tail'
            >
              {/* eslint-disable-next-line no-nested-ternary */}
              {isCurrentUsersUpspace && type !== ProgramType.PROGRAM
                ? 'Your Group'
                : user
                ? program?.title ?? `${user?.firstName ?? ''} ${user?.lastName ?? ''}'s Group`
                : 'Loading'}
            </Text>
          </Box>

          <Pressable onPress={onOptionsMenuPress}>
            <Icon name='options-menu' color='white' size={24} />
          </Pressable>

          {isFocusMode && (
            <Box margin={{ left: 'md' }}>
              <Pressable
                onPress={() => {
                  focusMode.setGroup(undefined)
                  navigation.navigate(links.HOME_STACK, {
                    screen: links.FEED_PAGE,
                  })
                }}
              >
                <Icon name='x' color='white' size={24} />
              </Pressable>
            </Box>
          )}
        </Box>

        {isCurrentUsersUpspace && (
          <Box position='absolute' display='flex' width='100%' height='100%'>
            <EditableUpspaceCover programId={program?.id} showGradient />
            {type === ProgramType.PROGRAM && renderInfoButton(52, 13)}
          </Box>
        )}

        {!isCurrentUsersUpspace && (
          <>
            <Box
              testID='CoverImageContainer'
              position='absolute'
              display='flex'
              width='100%'
              height='100%'
              backgroundColor='greyLight'
            >
              <Image
                cacheKey={
                  program
                    ? // eslint-disable-next-line no-underscore-dangle
                      `${program?.id}-program-cover-${program?.cover?._original?.etag}-large`
                    : uuidv4()
                }
                resizeMode='cover'
                style={commonStyles.fullSize}
                source={{ uri: program?.cover?.url || defaultPostThumbnailUrl }}
              />
            </Box>

            <Box
              testID='GradientContainer'
              pointerEvents='none'
              position='absolute'
              display='flex'
              width='100%'
              height='100%'
              flexDirection='column'
              justifyContent='flex-start'
            >
              <LinearGradient
                style={styles.gradient}
                colors={[theme.colors.black50, 'transparent']}
                start={{ x: 0, y: 0 }}
                end={{ x: 0, y: 1 }}
              />
            </Box>

            {type === ProgramType.PROGRAM && renderInfoButton(16, 16)}
          </>
        )}
      </Box>

      <Box width='100%' flexDirection='column' padding={{ vertical: 'sm', horizontal: 'md' }}>
        <Box flexDirection='row' align='center' justifyContent='space-between'>
          <Box testID='AvatarUsernameContainer' flexDirection='row' align='center'>
            <Box height={35} width={70} justifyContent='flex-end'>
              <Box
                position='absolute'
                width={70}
                height={70}
                align='center'
                justifyContent='center'
                backgroundColor='white'
                style={styles.br35}
              >
                <Pressable onPress={navigateToProfile}>
                  <Avatar width={66} height={66} image={user?.avatar ?? defaultAvatarImage} />
                </Pressable>
              </Box>
            </Box>

            <Box margin='smd'>
              <PressableText
                fontSize='md'
                numberOfLines={1}
                color='black'
                onPress={navigateToProfile}
              >
                @{user?.username}
              </PressableText>
            </Box>
          </Box>

          {isCurrentUsersUpspace || hasSetupGroup ? (
            <Box testID='EditJoinButtonContainer'>
              <Pressable
                // disabled={
                //   !isCurrentUsersUpspace && (isSubscribed !== undefined || isSubscriptionLoading)
                // }
                // onPress={isCurrentUsersUpspace ? handleEdit : handleAddPendingMembership}
                onPress={handleEdit}
              >
                <Box
                  height={32}
                  align='center'
                  justifyContent='center'
                  borderRadius='xs'
                  padding={{ horizontal: 'md' }}
                  backgroundColor='v2primary'
                >
                  <Text bold color='white'>
                    {isCurrentUsersUpspace ? 'Edit' : buttonTitle}
                  </Text>
                </Box>
              </Pressable>
            </Box>
          ) : null}
        </Box>

        <Box margin={{ top: 'smd' }}>
          <Text fontSize='sm' color='greyDarker'>
            Leaderboard
          </Text>
          <Box
            testID='LeaderboardAndActionsContainer'
            flexDirection='row'
            align='center'
            justifyContent='space-between'
          >
            <Box testID='LeaderboardInfoContainer' pointerEvents='box-none'>
              {!isLeaderboardLoading && leaderboard?.length ? (
                <Pressable onPress={handleLeaderboardPress}>
                  <Box height={40} flexDirection='row' align='center' margin={{ top: 'sm' }}>
                    {leaderboard.slice(0, 3).map((leader, index) => {
                      return (
                        <Box
                          width={46}
                          height={46}
                          margin={{ right: 'sm' }}
                          align='center'
                          justifyContent='center'
                          backgroundColor='white'
                          style={{
                            borderWidth: 1,
                            borderRadius: 46 * 0.5,
                            borderColor: leaderboardPositionColors[index],
                          }}
                        >
                          <Avatar
                            width={40}
                            height={40}
                            image={leader?.avatar ?? defaultAvatarImage}
                          />
                        </Box>
                      )
                    })}
                    <Text>{` / ${leaderboard?.length ?? ''}`}</Text>
                  </Box>
                </Pressable>
              ) : (
                <Box flexDirection='row' flex={1} align='center'>
                  <Text fontSize='sm' color='greyDark'>
                    {isLeaderboardLoading ? 'Loading...' : 'No leaderboard stats to show'}
                  </Text>
                </Box>
              )}
            </Box>
            <Box testID='ActionButtonsContainer' flexDirection='row'>
              {!isCurrentUsersUpspace && (
                <Box testID='ChatButtonContainer' margin={{ right: 'smd' }}>
                  <Pressable onPress={createChatRoom}>
                    <Box
                      width={32}
                      height={32}
                      align='center'
                      justifyContent='center'
                      borderRadius='xs'
                      backgroundColor='greyLight'
                    >
                      <Icon size={18} color='black' name='message-circle' />
                    </Box>
                  </Pressable>
                </Box>
              )}
              <Box testID='ShareButtonContainer'>
                <Pressable onPress={onShare}>
                  <Box
                    width={32}
                    height={32}
                    align='center'
                    justifyContent='center'
                    borderRadius='xs'
                    backgroundColor='greyLight'
                  >
                    <Icon size={18} color='black' name='share' />
                  </Box>
                </Pressable>
              </Box>
            </Box>
          </Box>
        </Box>
      </Box>

      {program?.bio || program?.description ? (
        <Box margin={{ vertical: 'sm', horizontal: 'md' }} backgroundColor='white'>
          <Bio bio={(type === ProgramType.PROGRAM ? program?.description : program?.bio) ?? ''} />
        </Box>
      ) : null}

      {program?.sponsors?.length ? (
        <Box width='100%' margin={{ vertical: 'smd' }}>
          <Box padding={{ horizontal: 'md' }}>
            <Text fontSize='sm' color='greyDarker'>
              Sponsors
            </Text>
          </Box>
          <Box flexDirection={IS_WEB ? 'column' : 'row'}>
            <FlatList<string>
              horizontal
              keyExtractor={(s) => s}
              data={program?.sponsors}
              contentContainerStyle={{ paddingRight: theme.spacing.smd }}
              renderItem={({ item: id }) => {
                return <SponsorItem programId={id} />
              }}
            />
          </Box>
        </Box>
      ) : null}

      {type === ProgramType.PROGRAM ? (
        <Box width='100%' margin={{ bottom: 'smd' }}>
          <Box flexDirection={IS_WEB ? 'column' : 'row'}>
            <FlatList<string>
              horizontal
              keyExtractor={(s) => s}
              data={programTags}
              contentContainerStyle={{ paddingHorizontal: theme.spacing.md }}
              renderItem={({ item, index }) => {
                return (
                  <Box
                    height={40}
                    flexDirection='row'
                    align='center'
                    justifyContent='center'
                    margin={{ bottom: 'xs' }}
                  >
                    <Text fontSize='sm' color='v2primary' bold>
                      {item}
                    </Text>
                    {index + 1 !== programTags.length && (
                      <Box
                        width={4}
                        height={4}
                        borderRadius='sm'
                        backgroundColor='v2midGrey'
                        margin={{ horizontal: 'sm' }}
                      />
                    )}
                  </Box>
                )
              }}
            />
          </Box>
        </Box>
      ) : null}
    </Box>
  )
}
